import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Heart, ArrowRight, ShieldCheck, AlertCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { BrandLockup } from "@/components/BrandMark";
import { useSEO } from "@/lib/seo";
import { publicUrl } from "@/lib/seo";

/** Public family story page. NOINDEX is set via useSEO. */
export default function FamilyProfile() {
  const { slug } = useParams();
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/families/${slug}`);
        setProfile(data);
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    })();
  }, [slug]);

  useSEO({
    title: profile ? `${profile.caregiver_first_name}'s SonSpeak family` : "SonSpeak family",
    description: profile?.caregiver_story?.slice(0, 160),
    noindex: true,
    path: `/families/${slug}`,
  });

  return (
    <div className="min-h-screen dotted-bg" data-testid="family-profile-page">
      <header className="px-5 sm:px-10 py-5 border-b-[3px] border-[color:var(--ink)] flex items-center justify-between bg-[color:var(--cream)]">
        <BrandLockup size={180} />
        <Link to="/register" className="pill-btn primary"><ArrowRight size={14} /> Get started</Link>
      </header>

      <main className="max-w-2xl mx-auto px-5 sm:px-10 py-12 sm:py-16">
        {error && (
          <div className="brutal-card bg-[#FFE3E0] text-[color:var(--coral-deep)] p-4 mb-4 flex gap-2" data-testid="family-error">
            <AlertCircle size={18} className="shrink-0 mt-0.5" />
            <div>
              <div className="font-bold">{error}</div>
              <Link to="/community" className="pill-btn mt-3">Back to community</Link>
            </div>
          </div>
        )}

        {profile && (
          <article className="brutal-card bg-white p-6 sm:p-8" data-testid="family-card">
            <div className="display text-xs uppercase tracking-[0.3em] text-[color:var(--coral-deep)] font-bold mb-3 flex items-center gap-2">
              <Heart size={12} className="fill-current" /> A SonSpeak family
            </div>
            <h1 className="display text-3xl sm:text-4xl font-extrabold mb-4">
              {profile.caregiver_first_name}'s story
              {profile.child_first_name ? ` (and ${profile.child_first_name})` : ""}
            </h1>
            {profile.child_age_range && (
              <div className="text-sm text-[color:var(--ink-soft)] mb-5">
                {{
                  toddler: "Toddler",
                  preschool: "Preschool age",
                  "school-age": "School-age",
                  teen: "Teen",
                  adult: "Adult",
                }[profile.child_age_range] || profile.child_age_range}
              </div>
            )}
            <div
              className="text-base sm:text-lg leading-relaxed whitespace-pre-wrap"
              data-testid="family-story"
            >
              {profile.caregiver_story}
            </div>

            {/* Referral reward block removed per R4-G: no signup credits,
                no promo credits, ever. */}

            <div className="mt-6 text-xs text-[color:var(--ink-soft)] flex items-center gap-1.5">
              <ShieldCheck size={12} /> This page is unlisted and not indexed by search engines.
            </div>
          </article>
        )}
      </main>
      {void publicUrl}
    </div>
  );
}
